import { lazy, Suspense, useEffect, useState } from 'react';
import { MdArrowBack, MdChevronLeft, MdChevronRight, MdDownload, MdForward, MdZoomIn, MdZoomOut } from 'react-icons/md';
import { FullScreen } from './ui';
import useGestiFoto from './useGestiFoto';

const PdfPagine = lazy(() => import('./PdfPagine'));

export const ePdf = msg => /\.pdf$/i.test(msg?.media_name || '');
// Cosa si apre nel visore: foto, video e documenti con un file salvato.
export const apribile = msg => !!msg?.media_url && ['image', 'video', 'document'].includes(msg.message_type);

function nomeDi(msg) {
  if (msg.media_name) return msg.media_name;
  return { image: 'Foto', video: 'Video', document: 'Documento' }[msg.message_type] || 'Allegato';
}

// Foto e documenti si guardano qui, sopra la chat. Prima una foto apriva una
// scheda nuova e un PDF si scaricava soltanto (29/09/2026).
//
// Dal 04/10/2026 come su WhatsApp: nero a tutto schermo, barre sovrapposte che
// un tocco nasconde, la foto si tira giù per chiudere e si ingrandisce con due
// dita o col doppio tocco (useGestiFoto.js). Prima la barra bianca in alto
// rubava spazio e si chiudeva solo con la X.
export default function MediaViewer({ elementi, inizio = 0, onClose, onForward }) {
  const [indice, setIndice] = useState(inizio);
  const [barre, setBarre] = useState(true);
  const msg = elementi[indice];
  const foto = msg?.message_type === 'image';
  // Il PDF scorre in verticale: lì il trascinamento è suo, si chiude con la freccia.
  const conGesti = !!msg && !ePdf(msg) && msg.message_type !== 'video';
  const { vista, setVista, giu, inMovimento, gesti, azzera } = useGestiFoto({
    attivo: conGesti, onClose, onScorri: passo => vai(passo), onTocco: () => setBarre(b => !b),
  });
  const ingrandita = vista.s > 1;
  const vai = passo => {
    azzera();
    setIndice(i => Math.min(elementi.length - 1, Math.max(0, i + passo)));
  };

  useEffect(() => {
    const tasto = e => {
      if (e.key === 'ArrowLeft') vai(-1);
      if (e.key === 'ArrowRight') vai(1);
    };
    document.addEventListener('keydown', tasto);
    return () => document.removeEventListener('keydown', tasto);
  });
  // Su iPhone il pizzico ingrandirebbe tutta la pagina invece della foto.
  useEffect(() => {
    const ferma = e => e.preventDefault();
    document.addEventListener('gesturestart', ferma);
    return () => document.removeEventListener('gesturestart', ferma);
  }, []);

  if (!msg) return null;
  const nome = nomeDi(msg);
  const url = msg.media_url;
  const sfuma = Math.max(0.15, 1 - giu / 400);

  let contenuto;
  if (foto) {
    contenuto = <img src={url} alt={msg.body || nome} draggable={false} className="visore-foto"
      style={{ transform: `translate(${vista.x}px, ${vista.y + giu}px) scale(${vista.s * (1 - Math.min(giu, 400) / 1600)})` }} />;
  } else if (msg.message_type === 'video') {
    contenuto = <video src={url} controls autoPlay playsInline className="max-h-full max-w-full" />;
  } else if (ePdf(msg)) {
    contenuto = (
      <Suspense fallback={<p className="text-sm text-white">Apro il PDF…</p>}>
        <PdfPagine url={url} />
      </Suspense>
    );
  } else {
    contenuto = (
      <div className="mx-4 max-w-sm rounded-lg bg-surface p-6 text-center" style={{ transform: `translateY(${giu}px)` }}>
        <p className="text-4xl" aria-hidden="true">📄</p>
        <p className="mt-2 break-all font-medium text-ink">{nome}</p>
        <p className="mt-1 text-sm text-muted">L'anteprima non è disponibile per questo tipo di file: scaricalo per aprirlo.</p>
      </div>
    );
  }

  return (
    <FullScreen open onClose={onClose} label={nome} testa={false} pannelloClass="visore-pannello">
      <div className={`visore${inMovimento ? ' in-movimento' : ''}`} style={{ backgroundColor: `rgba(0,0,0,${sfuma})` }}>
        <div className={`visore-area${ePdf(msg) ? ' con-pdf' : ''}`} {...gesti}>{contenuto}</div>

        {barre && giu === 0 && <>
          <div className="visore-testa">
            <button type="button" className="visore-pulsante" aria-label="Chiudi" onClick={onClose}><MdArrowBack size={24} aria-hidden="true" /></button>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{nome}</p>
              {elementi.length > 1 && <p className="text-xs opacity-80">{indice + 1} di {elementi.length}</p>}
            </div>
            {foto && <button type="button" className="visore-pulsante" aria-label={ingrandita ? 'Adatta' : 'Ingrandisci'}
              onClick={() => setVista(ingrandita ? { s: 1, x: 0, y: 0 } : { s: 2.5, x: 0, y: 0 })}>
              {ingrandita ? <MdZoomOut size={23} aria-hidden="true" /> : <MdZoomIn size={23} aria-hidden="true" />}
            </button>}
            {onForward && msg.whatsapp_message_id && (
              <button type="button" className="visore-pulsante" aria-label="Inoltra" onClick={() => onForward(msg)}><MdForward size={23} aria-hidden="true" /></button>
            )}
            <a className="visore-pulsante" aria-label="Scarica" href={url} download={msg.media_name || undefined}><MdDownload size={23} aria-hidden="true" /></a>
          </div>
          {indice > 0 && (
            <button type="button" aria-label="Allegato precedente" onClick={() => vai(-1)} className="visore-freccia sinistra">
              <MdChevronLeft size={28} aria-hidden="true" />
            </button>
          )}
          {indice < elementi.length - 1 && (
            <button type="button" aria-label="Allegato successivo" onClick={() => vai(1)} className="visore-freccia destra">
              <MdChevronRight size={28} aria-hidden="true" />
            </button>
          )}
          {msg.body && msg.message_type !== 'document' && (
            <p className="visore-didascalia">{msg.body}</p>
          )}
        </>}
      </div>
    </FullScreen>
  );
}
